import * as React from "react";
import {translate} from "react-i18next";
import _ from "lodash";

// redux
import {connect} from "react-redux";

// router
import {RouteComponentProps} from "react-router-dom";

// services
import {
    Link,
    RoleDto,
    PrivilegeDto,
    ResourceListOfRoleDto,
    ResourceListOfPrivilegeDto, MenuDto
} from "../../../services/Client";

// components
import Button from "../../../components/Button";

import RolesTable from "./RolesTable";
import RoleCreationDialog from "./RoleCreationDialog";
import TextField from "../../../components/TextField";
import {getDtoArray} from "../AdminUtils";
import {renderErrorToast} from "../../../utils/FeedbackMessageUtils";
import AdminViewActions from "../../../redux/actions/AdminViewActions";
import GeneralActions from "../../../redux/actions/GeneralActions";
import {ITranslatedComponent} from "../../../redux/reducers/generalReducer";
import {
    selectClient,
    selectAdminArealinks,
    selectPrivileges,
    selectRoles
} from "../../../redux/selectors";
import {compose, Dispatch} from "redux";
import HATEOASClient from "../../../services/HATEOASClient";
import {IReduxState} from "../../../redux/IReduxState";

interface IMapStateToProps {
    adminAreaLinks?: MenuDto;
    client: HATEOASClient;
    roles?: ResourceListOfRoleDto;
    privileges?: ResourceListOfPrivilegeDto;
}

interface IMapDispatchToProps {
    addTranslationLocation: () => void;
    receivePrivileges: (privileges: ResourceListOfPrivilegeDto) => void;
    receiveRoles: (roles: ResourceListOfRoleDto) => void;
}

interface IOwnProps {

}

interface IRolesViewProps extends IOwnProps, IMapDispatchToProps, IMapStateToProps, ITranslatedComponent, RouteComponentProps<any> {
}

interface IRolesViewState {
    /** Whether or not we are waiting for a response from the back end. */
    loadingRoles: boolean;

    /** Whether or not we are waiting for a response from the back end. */
    loadingPrivileges: boolean;

    /** Whether or not to show the role creation dialog. */
    showRoleCreationDialog: boolean;

    /** Role name filter */
    filter: string | undefined;

    /** Roles that match the current filter */
    filteredRoles: RoleDto[];
}

class RolesView extends React.Component<IRolesViewProps, IRolesViewState> {
    constructor(props: IRolesViewProps) {
        super(props);

        this.state = {
            loadingRoles: false,
            loadingPrivileges: false,
            showRoleCreationDialog: false,
            filter: undefined,
            filteredRoles: getDtoArray<RoleDto>(this.props.roles)
        };
    }

    componentDidMount() {
        this.props.addTranslationLocation();

        if (!this.props.roles) {
            // we need a list of all available roles for the roles table and creation dialog.
            this.requestRoles();
        }

        if (!this.props.privileges) {
            // we need a list of all available privileges for the roles table and creation dialog.
            this.requestPrivileges();
        }
    }

    requestRoles = (): void => {
        if (!this.props.adminAreaLinks ||
            !this.props.client.GET_roleList(this.props.adminAreaLinks).canExecute()) {
            return;
        }

        this.setState({loadingRoles: true});
        this.props.client.GET_roleList(this.props.adminAreaLinks).execute()
            .then((value: ResourceListOfRoleDto) => {
                this.props.receiveRoles(value);

                let filteredRoles = value.elements || [];
                if (this.state.filter !== undefined) {
                    // select only the roles that match the filter
                    filteredRoles = this.applyFilter(filteredRoles, this.state.filter);
                }

                // request was successfully fulfilled; reset loading state
                this.setState({loadingRoles: false, filteredRoles: filteredRoles});
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:GET_ROLES",
                    details: reason,
                    timestamp: Date.now()
                });
                this.setState({
                    loadingRoles: false
                });
            });
    }

    requestPrivileges = (): void => {
        if (!this.props.adminAreaLinks ||
            !this.props.client.GET_privilegeList(this.props.adminAreaLinks).canExecute()) {
            return;
        }

        this.setState({loadingPrivileges: true});
        this.props.client.GET_privilegeList(this.props.adminAreaLinks).execute()
            .then((value: ResourceListOfPrivilegeDto) => {
                this.props.receivePrivileges(value);

                // request was successfully fulfilled; reset loading state
                this.setState({loadingPrivileges: false});
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:GET_PRIVILEGES",
                    details: reason,
                    timestamp: Date.now()
                });
                this.setState({
                    loadingPrivileges: false
                });
            });
    }

    getRoleDtos = (resOfRoles?: ResourceListOfRoleDto): RoleDto[] => {
        const dtos = resOfRoles && resOfRoles.elements && resOfRoles.elements;
        return dtos ? dtos : [];
    }

    getPrivilegeDtos = (resOfPrivileges?: ResourceListOfPrivilegeDto): PrivilegeDto[] => {
        const dtos = resOfPrivileges && resOfPrivileges.elements && resOfPrivileges.elements;
        return dtos ? dtos : [];
    }

    addRole = (role: RoleDto): void => {
        this.setState({loadingRoles: true});

        if (this.props.roles) {
            this.props.client.POST_role(this.props.roles).execute(role)
                .then((value: Link) => {
                    // role was created succesfully; refresh the redux store.
                    // requestRoles() takes care of resetting the loadingRoles state.
                    this.requestRoles();
                })
                .catch((reason: any) => {
                    window.scrollTo(0, 0);
                    renderErrorToast({
                        message: "ERRORS:CREATE_ROLE",
                        translationOptions: {roleName: role.name},
                        details: reason,
                        timestamp: Date.now()
                    });
                    this.setState({
                        loadingRoles: false
                    });
                });
        }
    }

    updateRole = (oldRole: RoleDto, newRole: RoleDto): void => {
        this.setState({loadingRoles: true});

        if (this.props.roles) {
            this.props.client.PATCH_role(oldRole).execute(newRole)
                .then((value: Link) => {
                    // role was updated succesfully; refresh the redux store.
                    // requestRoles() takes care of resetting the loadingRoles state.
                    this.requestRoles();
                })
                .catch((reason: any) => {
                    window.scrollTo(0, 0);
                    renderErrorToast({
                        message: "ERRORS:UPDATE_ROLE",
                        translationOptions: {roleName: oldRole.name},
                        details: reason,
                        timestamp: Date.now()
                    });
                    this.setState({
                        loadingRoles: false
                    });
                });
        }
    }

    deleteRole = (role: RoleDto): void => {
        this.setState({loadingRoles: true});

        if (this.props.roles) {
            this.props.client.DELETE_role(role).execute(role)
                .then((value: any) => {
                    // role was deleted succesfully; refresh the redux store.
                    // requestRoles() takes care of resetting the loadingRoles state.
                    this.requestRoles();
                })
                .catch((reason: any) => {
                    window.scrollTo(0, 0);
                    renderErrorToast({
                        message: "ERRORS:DELETE_ROLE",
                        translationOptions: {roleName: role.name},
                        details: reason,
                        timestamp: Date.now()
                    });
                    this.setState({
                        loadingRoles: false
                    });
                });
        }
    }

    renderTable = (loading: boolean, roles: RoleDto[], privileges: PrivilegeDto[]): JSX.Element => (
        <RolesTable
            roles={roles}
            privileges={privileges}
            loading={loading}
            optionalControls={{
                canUpdateRole: dto => this.props.client.PATCH_role(dto).canExecute(),
                canDeleteRole: dto => this.props.client.DELETE_role(dto).canExecute(),
            }}
            onUpdateRole={(oldRole: RoleDto, newRole: RoleDto) => {
                this.updateRole(oldRole, newRole);
            }}
            onDeleteRole={(role: RoleDto) => {
                this.deleteRole(role);
            }}
        />
    )

    renderRefreshBtn = (loading: boolean): JSX.Element => (
        <Button
            label={this.props.t("RELOAD")}
            labelPosition="after"
            icon={"refresh"}
            disabled={loading}
            onClick={() => {
                this.requestRoles();
                this.requestPrivileges();
            }}
        />
    )

    renderAddRoleBtn = (loading: boolean): JSX.Element => (
        <Button
            label={this.props.t("ADD_ROLE")}
            labelPosition="after"
            icon={"add"}
            disabled={loading}
            onClick={() => {
                this.setState({showRoleCreationDialog: true});
            }}
        />
    )

    renderAddRoleDlg = (roles: RoleDto[], privileges: PrivilegeDto[]): JSX.Element => (
        <RoleCreationDialog
            roles={roles}
            privileges={privileges}
            onClose={(newRole?: RoleDto) => {
                this.setState({showRoleCreationDialog: false});
                if (newRole) {
                    this.addRole(newRole);
                }
            }}
        />
    )

    applyFilter = (dtos: RoleDto[], filter: string): RoleDto[] => {
        const filterLowerCase = filter.toLowerCase();
        return dtos.filter(dto => _.includes(dto.name.toLowerCase(), filterLowerCase));
    }

    renderFilters = (): JSX.Element => (
        <TextField
            placeholder={this.props.t("FILTER_BY_NAME")}
            value={this.state.filter}
            onChange={(event: React.FormEvent<{}>, newValue: string) => {
                const filterValue = newValue !== "" ? newValue : undefined;
                let filteredDtos = getDtoArray<RoleDto>(this.props.roles);
                if (filterValue) {
                    filteredDtos = this.applyFilter(filteredDtos, filterValue);
                }
                this.setState({filter: filterValue, filteredRoles: filteredDtos});
            }}
        />
    )

    render() {
        const roles = this.getRoleDtos(this.props.roles);
        const privileges = this.getPrivilegeDtos(this.props.privileges);
        const filteredRoles = this.state.filteredRoles;
        return (
            <>
                {
                    this.props.roles &&
                    this.props.client.POST_role(this.props.roles).canExecute() &&
                    this.renderAddRoleBtn(this.state.loadingRoles)
                }
                {this.renderRefreshBtn(this.state.loadingRoles)}
                <br />
                {this.renderFilters()}
                {this.renderTable(this.state.loadingRoles || this.state.loadingPrivileges, filteredRoles, privileges)}
                {
                    this.state.showRoleCreationDialog &&
                    this.renderAddRoleDlg(roles, privileges)
                }
            </>
        );
    }
}

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    adminAreaLinks: selectAdminArealinks(state),
    client: selectClient(state),
    roles: selectRoles(state),
    privileges: selectPrivileges(state),
});

const mapDispatchToProps = (dispatch: Dispatch<IReduxState>): IMapDispatchToProps => ({
    addTranslationLocation: () => dispatch(GeneralActions.AddTranslationLocation({pattern: "admin/roles", substitutes: "Roles Management"})),
    receivePrivileges: (privileges: ResourceListOfPrivilegeDto) => dispatch(AdminViewActions.ReceivePrivileges(privileges)),
    receiveRoles: (roles: ResourceListOfRoleDto) => dispatch(AdminViewActions.ReceiveRoles(roles)),
});

export default compose<React.ComponentType<IOwnProps>>(
    translate("ADMIN_VIEW"),
    connect<IMapStateToProps, IMapDispatchToProps>(mapStateToProps, mapDispatchToProps)
)(RolesView);



// WEBPACK FOOTER //
// ./src/views/AdminView/AccessTab/RolesView.tsx